import { Observable, firstValueFrom } from 'rxjs'
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
} from 'rxjs/operators'
import {
  Component,
  DestroyRef,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import {
  MatBottomSheetRef,
  MAT_BOTTOM_SHEET_DATA,
} from '@angular/material/bottom-sheet'
import { MatButtonModule } from '@angular/material/button'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { Dialog } from '@angular/cdk/dialog'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { provideNativeDateAdapter } from '@angular/material/core'
import { TranslationService } from '../../../../../services/translation.service'
import { FprCategoryService } from '../../../../../services/fprCategory.service'
import {
  PRODUCT_MATERIAL_TYPE_PLASTIC_PRODUCT,
  PRODUCT_MATERIAL_TYPE_RAW_RESIN,
} from '../../../../../constants/plastics-declaration'
import {
  ProvisionalRow,
  ProvisionalRowType,
  ProvisionalPlasticRow,
  ProvisionalPlasticResin,
  FprMethodUsed,
  FprResinRow,
  AddProvisionalRowResponse,
} from '../../../../../types/reports'
import { NapcsCodeService } from '../../../../../services/napcsCode.service'
import { ReportService } from '../../../../../services/report.service'
import { SpinnerService } from '../../../../../services/spinner.service'
import { ErrorService } from '../../../../../services/error.service'
import { DialogService } from '../../../../../services/dialog.service'
import { ApiError } from '../../../../../types/apiError'

import {
  CALCULATION_METHOD_AVERAGE_BILL_OF_MATERIALS,
  CALCULATION_METHOD_FIXED_FACTOR,
} from '../../../../../constants/reports'

type ExternalRowSheetData = {
  reportId: string
  // SKUs of non-provisional (real) products already in the report.
  existingNonProvisionalSkus: string[]
}

type ResinFormValue = {
  resinType: string
  resinSource: string
  weightPerUnitKg: number
}

@Component({
  selector: 'app-external-row-sheet',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatButtonModule,
    MatIconModule,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './external-row-sheet.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './external-row-sheet.component.scss',
})
export class ExternalRowSheetComponent implements OnInit {
  private readonly bottomSheetRef = inject(
    MatBottomSheetRef<
      ExternalRowSheetComponent,
      AddProvisionalRowResponse | undefined
    >,
  )

  private readonly sheetData = inject<ExternalRowSheetData>(
    MAT_BOTTOM_SHEET_DATA,
  )

  private readonly reportService = inject(ReportService)
  private readonly spinnerService = inject(SpinnerService)
  private readonly errorService = inject(ErrorService)
  private readonly dialogService = inject(DialogService)
  private readonly dialog = inject(Dialog)
  private readonly destroyRef = inject(DestroyRef)

  translationService = inject(TranslationService)

  private readonly napcsCodeService: NapcsCodeService = inject(NapcsCodeService)

  napcsCodes: Array<{ id: string; fullResinTitle: string }> = []
  napcsResinTitles: string[] = []

  SOURCE_TYPES: Record<string, string> = Object.create(null)
  sourceTypeKeys: string[] = []

  private readonly fprCategoryService = inject(FprCategoryService)

  packagingProductCtrl = new FormControl<boolean>(false, { nonNullable: true })

  private isPackagingProduct = false

  private readonly existingNonProvisionalSkuSet = new Set<string>()

  readonly abomAllowed$: Observable<boolean>

  readonly CALCULATION_METHOD_AVERAGE_BILL_OF_MATERIALS =
    CALCULATION_METHOD_AVERAGE_BILL_OF_MATERIALS

  readonly CALCULATION_METHOD_FIXED_FACTOR = CALCULATION_METHOD_FIXED_FACTOR

  readonly PLASTIC_MATERIAL_TYPE = PRODUCT_MATERIAL_TYPE_PLASTIC_PRODUCT
  readonly RAW_RESIN_MATERIAL_TYPE = PRODUCT_MATERIAL_TYPE_RAW_RESIN

  categories: Record<string, string> = Object.create(null)
  subcategoriesForSelectedCategory: Record<string, string> = Object.create(null)

  categoryKeys: string[] = []
  subcategoryKeys: string[] = []

  typeCtrl = new FormControl<ProvisionalRowType | ''>('', {
    nonNullable: true,
    validators: [Validators.required],
  })

  skuCtrl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required],
  })

  productIdentifierCtrl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required],
  })

  supplierCtrl = new FormControl<string>('', { nonNullable: true })

  productNameCtrl = new FormControl<string>('', { nonNullable: true })
  categoryCtrl = new FormControl<string>('', { nonNullable: true })
  subCategoryCtrl = new FormControl<string>('', { nonNullable: true })

  methodUsedCtrl = new FormControl<FprMethodUsed>(
    CALCULATION_METHOD_AVERAGE_BILL_OF_MATERIALS as FprMethodUsed,
    {
      nonNullable: true,
      validators: [Validators.required],
    },
  )

  productWeightKgCtrl = new FormControl<number | null>(null)

  resinTypeCtrl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required],
  })

  resinSourceCtrl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required],
  })

  resinsArray = new FormArray<
    FormGroup<{
      resinType: FormControl<string>
      resinSource: FormControl<string>
      weightPerUnitKg: FormControl<number>
    }>
  >([])

  form = new FormGroup({
    packagingProduct: this.packagingProductCtrl,
    type: this.typeCtrl,
    sku: this.skuCtrl,
    productIdentifier: this.productIdentifierCtrl,
    supplier: this.supplierCtrl,
    productName: this.productNameCtrl,
    category: this.categoryCtrl,
    subCategory: this.subCategoryCtrl,
    methodUsed: this.methodUsedCtrl,
    productWeightKg: this.productWeightKgCtrl,
    resinType: this.resinTypeCtrl,
    resinSource: this.resinSourceCtrl,
    resins: this.resinsArray,
  })

  constructor() {
    this.abomAllowed$ = this.skuCtrl.valueChanges.pipe(
      startWith(this.skuCtrl.value),
      debounceTime(250),
      map((sku) => (sku ?? '').trim()),
      distinctUntilChanged(),
      map((sku) => this.canUseAbomForSku(sku)),
    )

    this.fetchNapcsCodes()

    this.SOURCE_TYPES = {
      'Post-Consumer Recycled':
        this.translationService.getSourceTypePostConsumerRecycledText(),
      'Post-Industrial Recycled':
        this.translationService.getSourceTypePostIndustrialRecycledText(),
      'Virgin Fossil-Based':
        this.translationService.getSourceTypeVirginFossilText(),
      'Virgin Bio-Based': this.translationService.getSourceTypeVirginBioText(),
    }
    this.sourceTypeKeys = Object.keys(this.SOURCE_TYPES)
  }

  ngOnInit(): void {
    this.initExistingSkuSet()
    this.setupSubscriptions()
    this.syncCategoryMaps()
  }

  /**
   * Loads the non-provisional SKUs passed in from the parent component.
   */
  private initExistingSkuSet(): void {
    const incomingSkus = (this.sheetData?.existingNonProvisionalSkus ?? [])
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s !== '')

    for (const s of incomingSkus) {
      this.existingNonProvisionalSkuSet.add(s)
    }
  }

  /**
   * Wires up form subscriptions for dynamic behaviour.
   */
  private setupSubscriptions(): void {
    this.methodUsedCtrl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((m) => {
        if (this.typeCtrl.value !== this.PLASTIC_MATERIAL_TYPE) {
          this.resinsArray.clear()
          return
        }

        const isFixedFactor =
          m === (CALCULATION_METHOD_FIXED_FACTOR as FprMethodUsed)

        if (isFixedFactor) {
          this.ensureInitialResinRow()
          return
        }

        this.resinsArray.clear()
      })

    this.packagingProductCtrl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((checked) => {
        this.isPackagingProduct = !!checked
        this.syncCategoryMaps()
      })

    this.categoryCtrl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((cat) => {
        const { subcategoriesForSelectedCategory, sanitizedSubcategory } =
          this.fprCategoryService.getSubcategorySelection(
            cat,
            this.subCategoryCtrl.value,
            this.isPackagingProduct,
          )

        this.subcategoriesForSelectedCategory = subcategoriesForSelectedCategory
        this.subcategoryKeys = Object.keys(
          this.subcategoriesForSelectedCategory,
        )

        if (sanitizedSubcategory !== this.subCategoryCtrl.value) {
          this.subCategoryCtrl.setValue('')
        }
      })

    this.typeCtrl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((t) => {
        if (t === this.PLASTIC_MATERIAL_TYPE) {
          this.productWeightKgCtrl.clearValidators()
          this.productWeightKgCtrl.setValue(null, { emitEvent: false })
          this.productWeightKgCtrl.updateValueAndValidity({ emitEvent: false })

          const isFixedFactor =
            this.methodUsedCtrl.value ===
            (CALCULATION_METHOD_FIXED_FACTOR as FprMethodUsed)
          if (isFixedFactor) {
            this.ensureInitialResinRow()
          } else {
            this.resinsArray.clear()
          }

          this.syncCategoryMaps()
          return
        }

        this.resinsArray.clear()

        this.packagingProductCtrl.setValue(false, { emitEvent: false })
        this.isPackagingProduct = false
        this.categoryCtrl.setValue('')
        this.subCategoryCtrl.setValue('')
        this.categories = Object.create(null)
        this.subcategoriesForSelectedCategory = Object.create(null)
        this.categoryKeys = []
        this.subcategoryKeys = []

        if (t === PRODUCT_MATERIAL_TYPE_RAW_RESIN) {
          this.productWeightKgCtrl.setValidators([Validators.required])
          this.productWeightKgCtrl.updateValueAndValidity({ emitEvent: false })
          return
        }

        this.productWeightKgCtrl.clearValidators()
        this.productWeightKgCtrl.setValue(null, { emitEvent: false })
        this.productWeightKgCtrl.updateValueAndValidity({ emitEvent: false })
      })
  }

  isPlasticProduct(): boolean {
    return this.typeCtrl.value === this.PLASTIC_MATERIAL_TYPE
  }

  isRawResin(): boolean {
    return this.typeCtrl.value === PRODUCT_MATERIAL_TYPE_RAW_RESIN
  }

  /**
   * Loads NAPCS resin titles for the resin dropdown.
   */
  private fetchNapcsCodes(): void {
    this.napcsCodeService.getAll().subscribe({
      next: (codes) => {
        this.napcsCodes = (codes ?? []) as Array<{
          id: string
          fullResinTitle: string
        }>
        this.napcsResinTitles = this.napcsCodes
          .map((c) => c.fullResinTitle)
          .filter((t) => !!t)
      },
      error: () => {
        this.napcsCodes = []
        this.napcsResinTitles = []
      },
    })
  }

  /**
   * Refreshes category + subcategory options based on the current toggles.
   */
  private syncCategoryMaps(): void {
    this.categories = this.fprCategoryService.getCategories(
      this.isPackagingProduct,
    )
    this.categoryKeys = Object.keys(this.categories)

    if (this.categoryCtrl.value && !this.categories[this.categoryCtrl.value]) {
      this.categoryCtrl.setValue('')
      this.subCategoryCtrl.setValue('')
    }

    const { subcategoriesForSelectedCategory, sanitizedSubcategory } =
      this.fprCategoryService.getSubcategorySelection(
        this.categoryCtrl.value,
        this.subCategoryCtrl.value,
        this.isPackagingProduct,
      )

    this.subcategoriesForSelectedCategory = subcategoriesForSelectedCategory
    this.subcategoryKeys = Object.keys(this.subcategoriesForSelectedCategory)

    if (sanitizedSubcategory !== this.subCategoryCtrl.value) {
      this.subCategoryCtrl.setValue('')
    }
  }

  /**
   * Makes sure we always have at least one resin row to edit.
   */
  private ensureInitialResinRow() {
    if (this.resinsArray.length === 0) {
      this.addResin()
    }
  }

  /**
   * Creates a new resin form group, optionally prefilled.
   */
  private newResinGroup(v?: Partial<ResinFormValue>) {
    return new FormGroup({
      resinType: new FormControl<string>(v?.resinType ?? '', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      resinSource: new FormControl<string>(v?.resinSource ?? '', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      weightPerUnitKg: new FormControl<number>(v?.weightPerUnitKg ?? 0, {
        nonNullable: true,
        validators: [Validators.required],
      }),
    })
  }

  /**
   * Adds a new empty resin row to the list.
   */
  addResin() {
    this.resinsArray.push(this.newResinGroup())
  }

  /**
   * Removes a resin row by index, or resets if only one remains.
   */
  removeResin(idx: number) {
    if (idx < 0 || idx >= this.resinsArray.length) return

    if (this.resinsArray.length === 1) {
      this.resinsArray
        .at(0)
        .reset({ resinType: '', resinSource: '', weightPerUnitKg: 0 })
      return
    }

    this.resinsArray.removeAt(idx)
  }

  /**
   * Whether the form is valid and ready to submit.
   */
  get canSubmit(): boolean {
    if (this.typeCtrl.invalid) return false
    if (this.skuCtrl.invalid) return false
    if (this.productIdentifierCtrl.invalid) return false
    if (this.methodUsedCtrl.invalid) return false

    if (this.typeCtrl.value === this.PLASTIC_MATERIAL_TYPE) {
      const isFixedFactor =
        this.methodUsedCtrl.value ===
        (CALCULATION_METHOD_FIXED_FACTOR as FprMethodUsed)

      if (isFixedFactor) {
        if (this.resinsArray.length === 0) return false
        if (this.resinsArray.invalid) return false
      }
    }

    if (this.typeCtrl.value === PRODUCT_MATERIAL_TYPE_RAW_RESIN) {
      if (this.productWeightKgCtrl.invalid) return false
      if (this.resinTypeCtrl.invalid) return false
      if (this.resinSourceCtrl.invalid) return false
    }

    return true
  }

  /**
   * Closes the sheet without saving changes.
   */
  onCancel() {
    this.bottomSheetRef.dismiss(undefined)
  }

  /**
   * Handles form submit, creates the row and resets for next entry.
   */
  async onSubmit() {
    if (!this.canSubmit) return

    if (
      this.typeCtrl.value === this.PLASTIC_MATERIAL_TYPE &&
      this.methodUsedCtrl.value ===
        (CALCULATION_METHOD_AVERAGE_BILL_OF_MATERIALS as FprMethodUsed)
    ) {
      const sku = this.skuCtrl.value.trim()
      if (!this.canUseAbomForSku(sku)) {
        this.dialogService.showDialog(
          this.dialog,
          this.translationService.getReportsText(),
          this.translationService.getAbomRequiresExistingSkuText(),
        )
        return
      }
    }

    if (
      this.typeCtrl.value === this.PLASTIC_MATERIAL_TYPE &&
      this.methodUsedCtrl.value ===
        (CALCULATION_METHOD_FIXED_FACTOR as FprMethodUsed) &&
      this.resinsArray.length === 0
    ) {
      this.resinsArray.setErrors({ minLength: true })
      return
    }

    const reportId = this.sheetData?.reportId
    if (!reportId || reportId.trim() === '') {
      // shouldn't happen but added this as a defensive check nevertheless.
      this.bottomSheetRef.dismiss(undefined)
      return
    }

    const payload = this.buildProvisionalRowPayload()

    try {
      this.spinnerService.startSpinner()

      // Save and keep the sheet open so they can add more.
      await firstValueFrom(
        this.reportService.addProvisionalRow(reportId, payload),
      )

      this.spinnerService.stopSpinner()

      // Clear fields for the next row.
      this.resetForNextCreate()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Builds the payload we send to the API for this external row.
   */
  private buildProvisionalRowPayload(): ProvisionalRow {
    const type = this.typeCtrl.value
    const supplier = this.supplierCtrl.value.trim()

    if (type === this.PLASTIC_MATERIAL_TYPE) {
      const isFixedFactor =
        this.methodUsedCtrl.value ===
        (CALCULATION_METHOD_FIXED_FACTOR as FprMethodUsed)

      const plasticResins: Array<ProvisionalPlasticResin> = isFixedFactor
        ? this.resinsArray.controls.map((g) => {
            const v = g.getRawValue()
            const selected = (v.resinType ?? '').trim()
            const napcsId =
              this.napcsCodes.find((c) => c.fullResinTitle === selected)?.id ??
              ''
            return {
              napcsId,
              resinType: selected,
              sourceType: v.resinSource,
              weightPerUnitKg: v.weightPerUnitKg,
            }
          })
        : []

      const row: ProvisionalPlasticRow = {
        id: '',
        productIdentifier: this.productIdentifierCtrl.value.trim(),
        productName: this.productNameCtrl.value.trim(),
        supplier: supplier === '' ? null : supplier,
        category: this.categoryCtrl.value.trim(),
        subCategory: this.subCategoryCtrl.value.trim(),

        sku: this.skuCtrl.value.trim(),
        isTotalRow: false,
        childRowIds: [],
        methodLocked: false,
        qtyLocked: false,
        eolBreakdownLocked: false,

        methodUsed: this.methodUsedCtrl.value,
        calculationMethodOther: null,

        qtyManufactured: 0,
        qtyImported: 0,
        qtyPlacedOnMarket: 0,

        qtyAlberta: 0,
        qtyBritishColumbia: 0,
        qtyManitoba: 0,
        qtyNewBrunswick: 0,
        qtyNewfoundlandAndLabrador: 0,
        qtyNovaScotia: 0,
        qtyOntario: 0,
        qtyPrinceEdwardIsland: 0,
        qtyQuebec: 0,
        qtySaskatchewan: 0,
        qtyNorthwestTerritories: 0,
        qtyNunavut: 0,
        qtyYukon: 0,

        allowedMethods: [],

        qtyWasteGeneratedAtPremises: 0,
        qtyCollectedAtEndOfLife: 0,
        qtyDivertedDirectReuse: 0,
        qtyDivertedRefurbishment: 0,
        qtyDivertedRepair: 0,
        qtyDivertedRemanufacturing: 0,
        qtyDivertedMechanicalRecycling: 0,
        qtyDivertedChemicalRecycling: 0,
        qtyDivertedProcessingIntoChemicalsFuels: 0,
        qtyDivertedComposting: 0,
        qtyDivertedIncinerationWithEnergyRecovery: 0,
        qtyDivertedIncinerationIndustrialProcesses: 0,
        qtyFinalDisposalIncinerationNoEnergy: 0,
        qtyFinalDisposalLandfill: 0,

        isProvisional: true,

        plasticResins,
      }

      return { productMaterialType: PRODUCT_MATERIAL_TYPE_PLASTIC_PRODUCT, row }
    }

    const row: FprResinRow = {
      id: '',
      productIdentifier: this.productIdentifierCtrl.value.trim(),
      sku: this.skuCtrl.value.trim() || null,
      productName: this.productNameCtrl.value.trim(),
      supplier: supplier === '' ? null : supplier,

      resinType: this.resinTypeCtrl.value.trim(),
      resinSource: this.resinSourceCtrl.value.trim() || null,

      qtyManufactured: 0,
      qtyImported: 0,
      qtyPlacedOnMarket: 0,
      qtyAlberta: 0,
      qtyBritishColumbia: 0,
      qtyManitoba: 0,
      qtyNewBrunswick: 0,
      qtyNewfoundlandAndLabrador: 0,
      qtyNovaScotia: 0,
      qtyOntario: 0,
      qtyPrinceEdwardIsland: 0,
      qtyQuebec: 0,
      qtySaskatchewan: 0,
      qtyNorthwestTerritories: 0,
      qtyNunavut: 0,
      qtyYukon: 0,

      productWeightKg: this.productWeightKgCtrl.value,
      isProvisional: true,
    }

    return { productMaterialType: PRODUCT_MATERIAL_TYPE_RAW_RESIN, row }
  }

  /**
   * Resets fields after a successful create so they can add another.
   */
  private resetForNextCreate(): void {
    // Keep type + method so adding several rows is faster.
    const keepType = this.typeCtrl.value
    const keepMethod = this.methodUsedCtrl.value
    const keepPackaging = this.packagingProductCtrl.value

    // Common fields
    this.skuCtrl.setValue('')
    this.productIdentifierCtrl.setValue('')
    this.supplierCtrl.setValue('')
    this.productNameCtrl.setValue('')

    if (keepType === this.PLASTIC_MATERIAL_TYPE) {
      // Packaging toggles the category list.
      this.packagingProductCtrl.setValue(keepPackaging, { emitEvent: false })

      this.categoryCtrl.setValue('')
      this.subCategoryCtrl.setValue('')

      // Back to one empty resin row.
      this.resinsArray.clear()
      this.addResin()

      // Refresh category dropdowns.
      this.isPackagingProduct = !!keepPackaging
      this.syncCategoryMaps()
    } else {
      // Raw resin-only fields
      this.packagingProductCtrl.setValue(false, { emitEvent: false })
      this.isPackagingProduct = false

      this.categoryCtrl.setValue('')
      this.subCategoryCtrl.setValue('')
      this.categories = Object.create(null)
      this.subcategoriesForSelectedCategory = Object.create(null)
      this.categoryKeys = []
      this.subcategoryKeys = []

      this.productWeightKgCtrl.setValue(null)
      this.resinTypeCtrl.setValue('')
      this.resinSourceCtrl.setValue('')
    }

    // Restore what we kept
    this.typeCtrl.setValue(keepType, { emitEvent: false })
    this.methodUsedCtrl.setValue(keepMethod, { emitEvent: false })

    // Reset form state
    this.form.markAsPristine()
    this.form.markAsUntouched()
  }

  /**
   * Returns true when ABOM is allowed for this SKU.
   */
  canUseAbomForSku(sku: string): boolean {
    const s = (sku ?? '').trim().toLowerCase()
    if (s === '') return false
    return this.existingNonProvisionalSkuSet.has(s)
  }
}
